/**
 * ResizableSplitPane Component
 *
 * A horizontal split pane with a draggable divider for resizing.
 * Supports localStorage persistence of width and smooth transitions.
 *
 * @example
 * <ResizableSplitPane
 *   leftPanel={<Canvas />}
 *   rightPanel={<ChatPanel />}
 *   rightPanelVisible={showChat}
 *   storageKey="studio-split-width"
 * />
 */

import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react'
import './ResizableSplitPane.css'

interface ResizableSplitPaneProps {
  /** Content for the left panel */
  leftPanel: React.ReactNode
  /** Content for the right panel */
  rightPanel: React.ReactNode
  /** Whether the right panel is visible */
  rightPanelVisible: boolean
  /**
   * Which side uses the fixed percentage width (the resizable narrow column).
   * - 'right' (default): left flexes, right uses width % — e.g. canvas | chat.
   * - 'left': left uses width %, right flexes — e.g. chat | canvas (CWB-style builder chat).
   */
  fixedWidthSide?: 'left' | 'right'
  /** Default width of the fixed side as percentage (default: 50) */
  defaultRightWidth?: number
  /** Minimum width as percentage (default: 30) */
  minWidth?: number
  /** Maximum width as percentage (default: 70) */
  maxWidth?: number
  /** localStorage key for persistence (optional) */
  storageKey?: string
  /** Callback when resize occurs */
  onResize?: (rightWidth: number) => void
  /** Additional class name for the container */
  className?: string
  /** Optional external override for the width percentage */
  forceWidthPct?: number
  /**
   * When true, the right panel stays mounted in the React tree even when
   * hidden (display:none). Lets the caller preserve in-flight state — e.g. a
   * streaming chat session — across visibility toggles instead of tearing
   * down the component each time.
   */
  keepRightMounted?: boolean
}

const TRANSITION_DURATION = 200 // ms

export const ResizableSplitPane: React.FC<ResizableSplitPaneProps> = memo(
  ({
    leftPanel,
    rightPanel,
    rightPanelVisible,
    fixedWidthSide = 'right',
    defaultRightWidth = 50,
    minWidth = 30,
    maxWidth = 70,
    storageKey,
    onResize,
    className,
    forceWidthPct,
    keepRightMounted = false,
  }) => {
    // Load persisted width from localStorage
    const getInitialWidth = useCallback((): number => {
      if (storageKey && typeof window !== 'undefined') {
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          const parsed = Number.parseFloat(stored)
          if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
            return parsed
          }
        }
      }
      return defaultRightWidth
    }, [storageKey, defaultRightWidth, minWidth, maxWidth])

    const [fixedWidthPct, setFixedWidthPct] = useState<number>(getInitialWidth)
    const [isDragging, setIsDragging] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)

    const containerRef = useRef<HTMLDivElement>(null)
    const dragStartX = useRef<number>(0)
    const dragStartWidth = useRef<number>(0)
    const containerWidthRef = useRef<number>(0)

    // Persist width to localStorage
    useEffect(() => {
      if (storageKey && rightPanelVisible) {
        localStorage.setItem(storageKey, fixedWidthPct.toString())
      }
    }, [fixedWidthPct, storageKey, rightPanelVisible])

    // Handle visibility transition
    useEffect(() => {
      setIsTransitioning(true)
      const timer = setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }, [rightPanelVisible])

    // Mouse down on divider
    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        // If a parent is forcing the width (e.g. dual-panel widen), adopt it
        // as the new persisted base before drag starts. Otherwise the render
        // would jump from forceWidthPct → fixedWidthPct on mousedown because
        // the override is suspended while isDragging is true.
        const currentVisibleWidth =
          forceWidthPct !== undefined ? forceWidthPct : fixedWidthPct
        if (forceWidthPct !== undefined && forceWidthPct !== fixedWidthPct) {
          setFixedWidthPct(forceWidthPct)
        }
        setIsDragging(true)
        dragStartX.current = e.clientX
        dragStartWidth.current = currentVisibleWidth
        if (containerRef.current) {
          containerWidthRef.current = containerRef.current.getBoundingClientRect().width
        }
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
      },
      [fixedWidthPct, forceWidthPct]
    )

    // Mouse move during drag
    useEffect(() => {
      if (!isDragging) return

      const handleMouseMove = (e: MouseEvent) => {
        if (!containerWidthRef.current) return

        const containerWidth = containerWidthRef.current
        const deltaX =
          fixedWidthSide === 'left'
            ? e.clientX - dragStartX.current
            : dragStartX.current - e.clientX
        const deltaPercent = (deltaX / containerWidth) * 100

        let newWidth = dragStartWidth.current + deltaPercent
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))

        setFixedWidthPct(newWidth)
        onResize?.(newWidth)
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }, [isDragging, fixedWidthSide, minWidth, maxWidth, onResize])

    // Keyboard accessibility for divider
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const step = 2 // percentage step
        let newWidth: number

        if (fixedWidthSide === 'left') {
          switch (e.key) {
            case 'ArrowLeft':
              newWidth = Math.max(minWidth, fixedWidthPct - step)
              break
            case 'ArrowRight':
              newWidth = Math.min(maxWidth, fixedWidthPct + step)
              break
            case 'Home':
              newWidth = minWidth
              break
            case 'End':
              newWidth = maxWidth
              break
            default:
              return
          }
        } else {
          switch (e.key) {
            case 'ArrowLeft':
              newWidth = Math.min(maxWidth, fixedWidthPct + step)
              break
            case 'ArrowRight':
              newWidth = Math.max(minWidth, fixedWidthPct - step)
              break
            case 'Home':
              newWidth = maxWidth
              break
            case 'End':
              newWidth = minWidth
              break
            default:
              return
          }
        }

        e.preventDefault()
        setFixedWidthPct(newWidth)
        onResize?.(newWidth)
      },
      [fixedWidthPct, fixedWidthSide, minWidth, maxWidth, onResize]
    )

    const effectiveWidthPct =
      forceWidthPct !== undefined && !isDragging ? forceWidthPct : fixedWidthPct
    const fixedWidthStr = rightPanelVisible ? `${effectiveWidthPct}%` : '0%'

    // Note: forceWidthPct is only an override applied at render time. We
    // intentionally do NOT call setFixedWidthPct here, otherwise the user's
    // persisted width would get overwritten when the override is temporary
    // (e.g. all-collapsed shrink-to-rail), and removing the override would
    // leave the pane stuck at the override width.

    // Compute styles
    const containerStyle = useMemo(
      () =>
        ({
          '--right-panel-width': fixedWidthSide === 'right' ? fixedWidthStr : '0%',
          '--left-panel-width': fixedWidthSide === 'left' ? fixedWidthStr : undefined,
          '--transition-duration': `${TRANSITION_DURATION}ms`,
        }) as React.CSSProperties,
      [fixedWidthStr, fixedWidthSide]
    )

    /** Right column forced to 0% (e.g. atom-light chat fully collapsed) — hide divider, keep panel mounted */
    const isRightZeroWidth =
      Boolean(rightPanelVisible) &&
      forceWidthPct !== undefined &&
      !isDragging &&
      forceWidthPct <= 0

    const containerClassName = useMemo(() => {
      const classes = ['resizable-split-pane']
      if (rightPanelVisible) classes.push('resizable-split-pane--right-visible')
      if (fixedWidthSide === 'left') classes.push('resizable-split-pane--fixed-left')
      if (isDragging) classes.push('resizable-split-pane--dragging')
      if (isTransitioning) classes.push('resizable-split-pane--transitioning')
      if (isRightZeroWidth) classes.push('resizable-split-pane--right-zero')
      if (className) classes.push(className)
      return classes.join(' ')
    }, [rightPanelVisible, fixedWidthSide, isDragging, isTransitioning, isRightZeroWidth, className])

    return (
      <div ref={containerRef} className={containerClassName} style={containerStyle}>
        {/* Left Panel */}
        <div className="resizable-split-pane__left">{leftPanel}</div>

        {/* Divider — hidden when right width is forced to 0 (chat collapsed to full canvas) */}
        {rightPanelVisible && !isRightZeroWidth && (
          <button
            type="button"
            className="resizable-split-pane__divider"
            onMouseDown={handleMouseDown}
            onKeyDown={handleKeyDown}
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={fixedWidthPct}
            aria-valuemin={minWidth}
            aria-valuemax={maxWidth}
            aria-label="Resize panels"
          >
            <div className="resizable-split-pane__divider-handle" />
          </button>
        )}

        {/* Right Panel — by default only mounts when visible (cheaper).
            With keepRightMounted, stays in the tree across toggles so callers
            can preserve in-flight state (e.g. an SSE stream) while hidden. */}
        <div
          className="resizable-split-pane__right"
          aria-hidden={!rightPanelVisible}
          style={
            keepRightMounted && !rightPanelVisible
              ? { display: 'none' }
              : undefined
          }
        >
          {(rightPanelVisible || keepRightMounted) && rightPanel}
        </div>
      </div>
    )
  }
)

ResizableSplitPane.displayName = 'ResizableSplitPane'

export default ResizableSplitPane
